import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldAlert,
  CheckCircle2,
  EyeOff,
  Ban,
  ChevronLeft,
  ChevronRight,
  MessageSquareWarning,
  Clock,
  AlertTriangle,
  UserX,
} from "lucide-react";
import { toast } from "sonner";

// Backend enum values (PrivateProjectMessage.moderationStatus): CLEAN | FLAGGED | MASKED | BANNED.
// We expose UX-friendly labels in the filter — "PENDING" maps to FLAGGED, "APPROVED" maps to CLEAN.
type StatusFilter = "all" | "PENDING" | "APPROVED" | "MASKED" | "BANNED";
type ModerationAction = "approve" | "mask" | "ban";

const FILTER_TO_BACKEND: Record<Exclude<StatusFilter, "all">, "CLEAN" | "FLAGGED" | "MASKED" | "BANNED"> = {
  PENDING: "FLAGGED",
  APPROVED: "CLEAN",
  MASKED: "MASKED",
  BANNED: "BANNED",
};

const ACTION_TO_BACKEND: Record<ModerationAction, "CLEAN" | "MASKED" | "BANNED"> = {
  approve: "CLEAN",
  mask: "MASKED",
  ban: "BANNED",
};

export default function MessageModeration() {
  const { user } = useAuth();
  const isSuperAdmin = user?.staffRole === "SUPER_ADMIN";
  const canAct = canWrite(user?.staffRole);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("PENDING");
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<ModerationAction>("approve");
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [selectedMessageBody, setSelectedMessageBody] = useState<string>("");
  const [reason, setReason] = useState("");
  const [isMutating, setIsMutating] = useState(false);
  const LIMIT = 20;

  const utils = trpc.useUtils();

  const listQuery = trpc.admin.messageModeration.listFlaggedMessages.useQuery({
    page,
    limit: LIMIT,
    moderationStatus: statusFilter === "all" ? undefined : FILTER_TO_BACKEND[statusFilter],
  });
  const data = listQuery.data ?? null;
  const isLoading = listQuery.isLoading;

  const statsQuery = trpc.admin.messageModeration.stats.useQuery();
  const stats = statsQuery.data ?? null;

  const moderateMutation = trpc.admin.messageModeration.moderateMessage.useMutation({
    onSuccess: () => {
      utils.admin.messageModeration.listFlaggedMessages.invalidate();
      utils.admin.messageModeration.stats.invalidate();
    },
  });

  function openAction(messageId: string, action: ModerationAction, body: string) {
    setSelectedMessageId(messageId);
    setSelectedAction(action);
    setSelectedMessageBody(body);
    setReason("");
    setActionDialogOpen(true);
  }

  function closeDialog() {
    setActionDialogOpen(false);
    setSelectedMessageId(null);
    setSelectedMessageBody("");
    setReason("");
  }

  async function submitAction() {
    if (!selectedMessageId) return;
    const note = reason.trim();
    if (selectedAction === "ban" && !note) return;
    setIsMutating(true);
    try {
      await moderateMutation.mutateAsync({
        messageId: selectedMessageId,
        action: ACTION_TO_BACKEND[selectedAction],
        reason: note || "(no note)",
      });
      const labels: Record<ModerationAction, string> = {
        approve: "Message validé et envoyé",
        mask: "Coordonnées masquées",
        ban: "Pro suspendu",
      };
      toast.success(labels[selectedAction]);
      closeDialog();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de la modération");
    } finally {
      setIsMutating(false);
    }
  }

  // Backend returns shape { messages, total, page, totalPages } — adapt to local view-model.
  const messages = (data?.messages ?? []).map((m) => ({
    id: m.id,
    createdAt: m.createdAt as unknown as string,
    companyName: m.Company?.name ?? "—",
    projectTitle: m.PrivateProject?.title ?? "—",
    body: m.body ?? "",
    riskScore: m.riskScore ?? 0,
    // backend doesn't expose an attempt counter yet — surface 0 until we wire it up
    attemptCount: 0,
    // expose UI-friendly status (FLAGGED is the default "pending" bucket from the table view)
    moderationStatus: m.moderationStatus === "FLAGGED" ? "PENDING" :
                       m.moderationStatus === "CLEAN" ? "APPROVED" :
                       m.moderationStatus,
  }));
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  function formatDate(d: string | Date | null | undefined) {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function riskBadge(score: number | null | undefined) {
    if (score == null) return <Badge variant="outline">—</Badge>;
    if (score >= 80) return <Badge variant="destructive">Élevé ({score})</Badge>;
    if (score >= 50)
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-200">
          Moyen ({score})
        </Badge>
      );
    return <Badge variant="outline">Faible ({score})</Badge>;
  }

  function statusBadge(status: string) {
    switch (status) {
      case "PENDING":
        return (
          <Badge variant="outline" className="text-yellow-600 border-yellow-300 bg-yellow-50">
            En attente
          </Badge>
        );
      case "APPROVED":
        return <Badge className="bg-green-50 text-green-600 border-green-200">Validé</Badge>;
      case "MASKED":
        return <Badge className="bg-blue-50 text-blue-600 border-blue-200">Masqué</Badge>;
      case "BANNED":
        return <Badge variant="destructive">Banni</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6 animate-pulse">
          <div className="h-8 bg-muted rounded w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-muted rounded" />
            ))}
          </div>
          <div className="h-64 bg-muted rounded" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-destructive" />
            Modération messages (anti-leak)
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Valider, masquer ou bannir les messages suspects contenant des coordonnées
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <Clock className="h-3.5 w-3.5" />
                En attente
              </div>
              <div className="text-2xl font-bold text-yellow-600">{stats?.pending ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Risque élevé
              </div>
              <div className="text-2xl font-bold text-destructive">{stats?.highRisk ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <MessageSquareWarning className="h-3.5 w-3.5" />
                Aujourd'hui
              </div>
              <div className="text-2xl font-bold">{stats?.todayCount ?? 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                <UserX className="h-3.5 w-3.5" />
                Récidivistes
              </div>
              <div className="text-2xl font-bold text-orange-600">
                {stats?.repeatOffenders ?? 0}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-3">
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setStatusFilter(v as StatusFilter);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="PENDING">En attente</SelectItem>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="APPROVED">Validés</SelectItem>
              <SelectItem value="MASKED">Masqués</SelectItem>
              <SelectItem value="BANNED">Bannis</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground ml-auto">
            {total} message{total !== 1 ? "s" : ""}
          </span>
        </div>

        <Card>
          <CardContent className="p-0">
            {messages.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground text-sm">
                Aucun message suspect à modérer
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Pro (expéditeur)</TableHead>
                    <TableHead>Projet</TableHead>
                    <TableHead className="max-w-[250px]">Message</TableHead>
                    <TableHead>Risque</TableHead>
                    <TableHead>Tentatives</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((msg) => (
                    <TableRow key={msg.id}>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(msg.createdAt)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {msg.companyName ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[120px] truncate">
                        {msg.projectTitle ?? "—"}
                      </TableCell>
                      <TableCell className="text-sm max-w-[250px]">
                        <p className="truncate" title={msg.body}>
                          {msg.body}
                        </p>
                      </TableCell>
                      <TableCell>{riskBadge(msg.riskScore)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant={msg.attemptCount > 3 ? "destructive" : "outline"}>
                          {msg.attemptCount ?? 0}
                        </Badge>
                      </TableCell>
                      <TableCell>{statusBadge(msg.moderationStatus)}</TableCell>
                      <TableCell className="text-right">
                        {canAct && msg.moderationStatus === "PENDING" ? (
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAction(msg.id, "approve", msg.body)}
                              title="Valider et envoyer"
                            >
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openAction(msg.id, "mask", msg.body)}
                              title="Masquer les coordonnées"
                            >
                              <EyeOff className="h-4 w-4 text-blue-600" />
                            </Button>
                            {(isSuperAdmin || user?.staffRole === "ADMIN_OPS") && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openAction(msg.id, "ban", msg.body)}
                                title="Bannir le pro"
                              >
                                <Ban className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {msg.moderationStatus !== "PENDING" ? "Traité" : "Lecture seule"}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Précédent
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Suivant
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}
      </div>

      <Dialog open={actionDialogOpen} onOpenChange={setActionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedAction === "approve" && "Valider ce message"}
              {selectedAction === "mask" && "Masquer les coordonnées"}
              {selectedAction === "ban" && "Bannir ce professionnel"}
            </DialogTitle>
            <DialogDescription>
              {selectedAction === "approve" &&
                "Le message sera envoyé tel quel au particulier."}
              {selectedAction === "mask" &&
                "Les numéros de téléphone et emails seront remplacés par [coordonnées masquées]."}
              {selectedAction === "ban" &&
                "Le compte du professionnel sera suspendu. Cette action est irréversible."}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md bg-muted p-3 text-sm max-h-32 overflow-y-auto">
            {selectedMessageBody}
          </div>
          {selectedAction === "ban" ? (
            <Textarea
              placeholder="Raison du bannissement (obligatoire)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          ) : (
            <Textarea
              placeholder="Note interne (optionnel)..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Annuler
            </Button>
            <Button
              variant={selectedAction === "ban" ? "destructive" : "default"}
              onClick={submitAction}
              disabled={(selectedAction === "ban" && !reason.trim()) || isMutating}
            >
              {selectedAction === "approve" && "Valider et envoyer"}
              {selectedAction === "mask" && "Masquer"}
              {selectedAction === "ban" && "Bannir le pro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
